import { createContext, useState, useEffect, ReactNode, useContext, useRef, useCallback } from 'react';
import axios from '../axios-config';

export interface OrganizationType {
    organizationId: string;
    organizationName: string;
    organizationTypeId: number;
    pointsAvailable: number;
    pointsAwarded: number;
    deActivatedOn?: string | null;
    // Some API payloads use a different casing/spelling
    deactivatedOn?: string | null;
    parentOrganizationId?: number | null;
    icon: string;
    color: string;
    accent: string;
    gemGradient: string;
    backgroundImageUrl: string;
    organizationUsers: any[]; // TODO: Define type
}

export interface OrganizationWithTotals extends OrganizationType {
    totalStudents: number;
    totalTeachers: number;
}

interface OrganizationsContextValue {
    organizations: OrganizationType[];
    fetchOrganizations: (options?: { force?: boolean }) => Promise<void>;
    schools: OrganizationWithTotals[];
    teachers: any[];
    students: any[];
}

export const OrganizationsContext = createContext<OrganizationsContextValue>({
    organizations: [],
    fetchOrganizations: async () => {},
    schools: [],
    teachers: [],
    students: []
});

export const OrganizationsProvider = ({ children }: { children: ReactNode }) => {
    const [Organizations, setOrganizations] = useState<OrganizationType[]>([]);
    const [, setIsLoading] = useState(false);
    const isInitializedRef = useRef(false);
    const isLoadingRef = useRef(false);
    const inflightRef = useRef<Promise<void> | null>(null);

    const fetchOrganizations = useCallback(async (options?: { force?: boolean }) => {
        const force = !!options?.force;

        if (isLoadingRef.current) {
            console.log("Skipping fetchOrganizations: Already loading");
            await (inflightRef.current ?? Promise.resolve());
            return;
        }

        if (!force && isInitializedRef.current) {
            console.log("Skipping fetchOrganizations: Already initialized");
            return;
        }

        console.log("Fetching organization types...");
        isLoadingRef.current = true;
        setIsLoading(true);

        const request = (async () => {
            try {
                const { data } = await axios.get('/api/organizations');
                setOrganizations(Array.isArray(data) ? data : (data.organizations ?? []));
                isInitializedRef.current = true;
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : String(err);
                console.error('Error fetching organization types:', message);
                setOrganizations([]);
                isInitializedRef.current = true;
            } finally {
                isLoadingRef.current = false;
                inflightRef.current = null;
                setIsLoading(false);
            }
        })();

        inflightRef.current = request;
        await request;
    }, []);

    const schools: OrganizationWithTotals[] = Organizations
        .filter(org => org.organizationTypeId === 1 || org.organizationTypeId === 2)
        .map(org => ({
            ...org,
            totalStudents: org.organizationUsers ? org.organizationUsers.filter((user: any) => user.acornUser.mainRole === "STUDENT").length : 0,
            totalTeachers: org.organizationUsers ? org.organizationUsers.filter((user: any) => user.acornUser.mainRole.includes("TEACHER")).length : 0
        }));

    const teachers = Organizations.flatMap(org =>
        org.organizationUsers ? org.organizationUsers.filter((user: any) => user.acornUser.mainRole.includes("TEACHER")) : []
    );
    const students = Organizations.flatMap(org =>
        org.organizationUsers ? org.organizationUsers.filter((user: any) => user.acornUser.mainRole === "STUDENT") : []
    );

    useEffect(() => {
        fetchOrganizations();
    }, [fetchOrganizations]); // Run only once on mount

    return (
        <OrganizationsContext.Provider value={{ organizations: Organizations, fetchOrganizations, schools, teachers, students }}>
            {children}
        </OrganizationsContext.Provider>
    );
};

export const useOrganizations = () => useContext(OrganizationsContext);
